import React, { useState, useEffect } from 'react';
import { X, Wind, AlertTriangle } from 'lucide-react';
import { calculateAdjustedPM25, calculateActualPM25 } from '../utils/calculations';
import { pmToAqi, getAqiCategory } from '../utils/airQualityUtils';
import Modal from './Modal';
import ChartToggle from './ChartToggle';
import { purpleAirIdToLocalId, faultySensors } from '../utils/constants';
import FaultySensorIndicator from './FaultySensorIndicator';


const StatusBadge = ({ text, color }) => (
  <div className={`px-3 py-1 rounded-full text-md font-medium bg-opacity-10`}>
    Status: <span className={`${color}`}>{text}</span>
  </div>
);

const SensorList = ({ sensorIds }) => (
  <div className="bg-gray-50 p-3 rounded-lg text-sm">
    <h3 className="font-medium mb-2">Sensor Locations:</h3>
    {sensorIds.map(id => {
      const info = purpleAirIdToLocalId[id];
      const isFaulty = faultySensors.has(Number(id));
      return (
        <div key={id} className="flex justify-between items-center mb-1 last:mb-0">
          <span className={`text-gray-600 flex items-center ${isFaulty ? 'text-red-700' : ''}`}>
            {isFaulty && <FaultySensorIndicator position={info?.position} />}
            {info?.position}:
          </span>
          <span className={`ml-2 ${isFaulty ? 'text-red-700' : 'text-gray-500'}`}>
            {id}
          </span>
        </div>
      );
    })}
  </div>
);

const formatChartData = (readings) => {
  if (!Array.isArray(readings)) return [];

  const groupedReadings = readings.reduce((acc, reading) => {
    const timestamp = new Date(reading.timestamp).toLocaleTimeString([], {
      hour: '2-digit',
      minute: '2-digit'
    });

    if (!acc[timestamp]) {
      acc[timestamp] = {
        timestamp,
        rawTimestamp: reading.timestamp,
        sensors: {}
      };
    }

    if (reading.pm25 > 0 && reading.pm25 <= 500) {
      acc[timestamp].sensors[reading.sensorId] = reading.pm25;
    }

    return acc;
  }, {});

  return Object.values(groupedReadings)
    .map(point => {
      const readings = Object.values(point.sensors);
      return {
        timestamp: point.timestamp,
        rawTimestamp: point.rawTimestamp,
        ...point.sensors,
        average: calculateAdjustedPM25(readings),
        rawAverage: calculateActualPM25(readings)
      };
    })
    .sort((a, b) => new Date(a.rawTimestamp) - new Date(b.rawTimestamp));
};

const getAirQualityStatus = (pm25) => {
  const { category, textColor } = getAqiCategory(pmToAqi(pm25));
  return {
    text: category,
    color: textColor
  };
};

const SlideToolbar = ({
  isOpen,
  onClose,
  locationData,
  statistics,
  timeRange,
  onTimeRangeChange,
  modalPosition,
  frameHeight,
  viewMode,
  onViewModeChange
}) => {
  const [sensorData, setSensorData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLocationData = async () => {
      if (!locationData?.id) return;

      try {
        setLoading(true);
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/sensors/data?` +
          new URLSearchParams({
            localId: locationData.id,
            startTime: timeRange
          })
        );

        if (!response.ok) throw new Error('Failed to fetch sensor data');

        const data = await response.json();
        setSensorData(data);
        setError(null);
      } catch (err) {
        console.error('Error fetching sensor data:', err);
        // Fail silently - keep showing existing data if available
        if (!sensorData) {
          setError('Failed to load sensor data');
        }
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && locationData?.id) {
      fetchLocationData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [locationData?.id, timeRange, isOpen]);

  if (!locationData) return null;

  const chartData = sensorData?.data ? formatChartData(sensorData.data) : [];
  const sensorIds = sensorData?.data ?
    [...new Set(sensorData.data.map(reading => reading.sensorId))] :
    [];

  const locationStats = statistics?.locations?.[locationData?.id];
  const averagePM25 = locationStats?.hour?.adjustedAverage;
  const rawAveragePM25 = locationStats?.hour?.rawAverage;
  const locationFullName = locationStats?.fullName || locationData?.name;
  const { text: aqiCategory, color: aqiColor } = getAirQualityStatus(averagePM25);

  return (
    <Modal isOpen={isOpen} onClose={onClose} modalPosition={modalPosition} frameHeight={frameHeight}>
      <div className="relative">
        <button
          onClick={onClose}
          className="absolute right-4 top-4 p-2 rounded-full hover:bg-gray-100 transition-colors z-20"
          aria-label="Close modal"
        >
          <X size={20} />
        </button>


        <div className="p-6 pt-14">
          <div className="mb-6 flex items-center justify-between">
            <div>
              <h2 className="text-xl font-bold">{locationFullName}</h2>
            </div>
            <StatusBadge text={aqiCategory} color={aqiColor} />
          </div>

          {loading && (
            <div className="flex justify-center items-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500" />
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
              <div className="flex items-center">
                <AlertTriangle className="text-red-500 mr-2" />
                <p className="text-red-700">{error}</p>
              </div>
            </div>
          )}

          {!loading && !error && (
            <div className="space-y-6">
              <div className="bg-gray-50 p-4 rounded-lg flex justify-between">
                <div>
                  <div className="flex justify-between items-start mb-2">
                    <div className="flex items-center gap-3">
                      <Wind size={20} className={aqiColor} />
                      <div className="flex flex-col">
                        <div className="flex items-baseline gap-2">
                          <span className="text-2xl font-bold">{pmToAqi(averagePM25)}</span>
                          <span className="text-gray-500">AQI</span>
                        </div>
                        <div className="text-sm text-gray-500">
                          PM2.5: {averagePM25?.toFixed(1) || '--'} µg/m³
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="text-sm text-gray-500 mt-2">
                    Location type: {locationStats?.type || 'Elementary School'}
                  </div>
                  <div className="text-sm text-gray-500 mt-1">
                    Sensor type: <a href="https://www2.purpleair.com/" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">Purple Air</a>
                  </div>
                  {rawAveragePM25 && (
                    <div className="text-sm text-gray-500 mt-1">
                      Raw PM2.5: {rawAveragePM25.toFixed(1)} µg/m³
                    </div>
                  )}
                </div>
                <div>
                  {sensorIds.length > 0 && <SensorList sensorIds={sensorIds} />}
                </div>
              </div>

              <div className="space-y-4">
                <h3 className="font-medium">Air Quality Data</h3>
                <ChartToggle
                  locationId={locationData?.id}
                  sensorIds={sensorIds}
                  chartData={chartData}
                  viewMode={viewMode}
                  onViewModeChange={onViewModeChange}
                  timeRange={timeRange}
                  onTimeRangeChange={onTimeRangeChange}
                />
              </div>
            </div>
          )}
        </div>
      </div>
    </Modal>
  );
};

export default SlideToolbar;